import type { ComponentPropsWithoutRef } from "react";
import { GLYPHS } from "./glyphs";

/**
 * The app's only icon set: Phosphor, one family, through <Icon name="…" />.
 * Names are stable app vocabulary, mapped to Phosphor icons in scripts/build-icons.ts, which
 * generates ./glyphs.ts with only the weights the app draws (run `npm run icons` after a change).
 */
export type IconName = keyof typeof GLYPHS;
/** Names that also come in the light weight (EmptyState's icons). */
export type LightIconName = { [K in IconName]: (typeof GLYPHS)[K] extends { light: unknown } ? K : never }[IconName];
export type IconWeight = "regular" | "bold" | "fill" | "light";

type Glyph = Partial<Record<IconWeight, string | string[]>> & { regular: string | string[] };

export type IconProps = Omit<ComponentPropsWithoutRef<"svg">, "fill" | "strokeWidth"> & {
  /** Pixels. DESIGN: 20 in rows and buttons, 22 in bar buttons, 24 in the dock. */
  size?: number;
  mirrored?: boolean;
  /** @deprecated Legacy stroke width from the hand-drawn set: >= 2.4 renders the bold weight. */
  strokeWidth?: number | string;
  /** @deprecated Legacy: any value other than "none" (e.g. "currentColor") renders the fill weight. */
  fill?: string;
} & (
    | {
        name: IconName;
        /** Default "regular" ("bold" when size <= 16); "fill" for the active tab and selected states. */
        weight?: Exclude<IconWeight, "light">;
      }
    | { name: LightIconName; weight: "light" }
  );

/** Decorative by default (aria-hidden); pass aria-label to make it a labelled image. */
export function Icon({ name, size = 24, weight, strokeWidth, fill, mirrored, color, children, ...rest }: IconProps) {
  const glyph: Glyph = GLYPHS[name] ?? GLYPHS.question;
  const resolved: IconWeight = weight ?? (fill && fill !== "none" ? "fill" : Number(strokeWidth) >= 2.4 || size <= 16 ? "bold" : "regular");
  const d = glyph[resolved] ?? glyph.regular;
  const labelled = rest["aria-label"] != null || rest["aria-labelledby"] != null;
  // The attributes Phosphor's IconBase renders (currentColor fill, 256 grid), then the caller's.
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      fill={color ?? "currentColor"}
      viewBox="0 0 256 256"
      transform={mirrored ? "scale(-1, 1)" : undefined}
      aria-hidden={labelled ? undefined : true}
      role={labelled ? "img" : undefined}
      focusable="false"
      {...rest}
    >
      {children}
      {typeof d === "string" ? <path d={d} /> : d.map((p) => <path key={p} d={p} />)}
    </svg>
  );
}
